import React, { useEffect, useMemo, useState } from "react";
import { Skeleton, useMediaQuery, useTheme } from "@mui/material";
import moment from "moment";
import DoughnutChart from "../../Analytics/Charts/DoughnutChart";
import { useGetUserDistributionBreakdownQuery } from "../../../features/analytics/analytics.services";
import DateFilterComponent from "../../../shared/DateFilterComponent/DateFilterforChart";
import ProgramTypeMetricsSkeleton from "../../../shared/Skeleton/AdminSkeleton/ProgramChartMetricsSkeleton";
import MuiTooltipWrapper from "../../Tooltip/Tooltip";
import { useNavigate } from "react-router-dom";

const now = new Date();
const formattedMonth = `[${now.getFullYear()}-${String(
  now.getMonth() + 1
).padStart(2, "0")}]`;

const formatDateForAPI = (date) => {
  if (!date) return null;
  return moment(date).format("YYYY-MM-DD");
};

const buildQueryParams = (filters, dateRange = null) => {
  if (filters.date === "all") {
    return undefined;
  }

  const params = {};

  if (filters.date === "custom") {
    if (dateRange?.startDate && dateRange?.endDate) {
      params.filter_by = "custom";
      params.start_date = formatDateForAPI(dateRange.startDate);
      params.end_date = formatDateForAPI(dateRange.endDate);
    }
  } else if (filters.date !== "all") {
    if (
      typeof filters.date === "string" &&
      filters.date.startsWith("[") &&
      filters.date.endsWith("]")
    ) {
      params.filter_by = "month";
      params.date = filters.date;
    } else {
      const isYear = /^\d{4}$/.test(filters.date);
      params.filter_by = isYear ? "year" : "month";
      params.date = filters.date;
    }
  }

  return Object.keys(params).length > 0 ? params : undefined;
};

const USER_BREAKDOWN_COLORS = [
  "#B8B3FF",
  "#6D6E8C",
  "#5FC4C4",
  "#0F766E",
  "#FBCB14",
  "#2F3945",
];

const constructUserBreakdown = (data) => {
  if (!data) return [];

  return data.map((item) => [item?.label, item?.count, item?.percent]);
};

const UserBreakdownChart = () => {
  const navigate = useNavigate();
  const [userBreakdownFilters, setUserBreakdownFilters] = useState({
    date: formattedMonth,
  });
  const [selectedDate, setSelectedDate] = useState(moment());
  const [viewDateMode, setViewDateMode] = useState("month");

  const userBreakdownParams = useMemo(
    () => buildQueryParams({ date: userBreakdownFilters.date }),
    [userBreakdownFilters.date]
  );

  const {
    data: userDistribution,
    isLoading,
    isFetching,
  } = useGetUserDistributionBreakdownQuery(userBreakdownParams, {
    refetchOnMountOrArgChange: true,
  });

  const handleUserBreakdownClick = (label) => {
    const tabMap = {
      Admin: "admin",
      Provider: "provider",
      "Medical Assistant": "medical_assistant",
      "Dental Assistant": "dental_assistant",
       HTA: "hta",
       "Workforce Supervisor": "payroll_supervisor",
    };
    const tab = tabMap[label];

    if (!tab) return;

    navigate(`/members?tab=${tab}`);
  };

  const VIEW_OPTIONS = [
    {
      value: "month",
      label: (
        <MuiTooltipWrapper
          title="View data for a specific month/months"
          placement="right"
        >
          <span>Month</span>
        </MuiTooltipWrapper>
      ),
    },
    {
      value: "year",
      label: (
        <MuiTooltipWrapper
          title="View data for a selected year"
          placement="right"
        >
          <span>Year</span>
        </MuiTooltipWrapper>
      ),
    },
  ];

  const handleDateChange = (date, viewMode, formatted) => {
    if (date !== null || viewMode === "all") {
      if (viewMode === "month" && formatted) {
        setUserBreakdownFilters({ date: `[${formatted}]` });
      } else if (viewMode === "year" && formatted) {
        setUserBreakdownFilters({ date: formatted });
      } else if (viewMode === "all") {
        setUserBreakdownFilters({ date: "all" });
      }

      setSelectedDate(date);
      setViewDateMode(viewMode === "all" ? "all" : viewMode);
    }
  };

  useEffect(() => {
    setUserBreakdownFilters({ date: formattedMonth });
  }, []);

  const chartData = [
    ["User Type", "Count", "Percentage"],
    ...constructUserBreakdown(userDistribution?.breakdown),
  ];

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isLaptop = useMediaQuery(theme.breakpoints.down("lg"));

  return (
    <div>
      {isLoading ? (
        <ProgramTypeMetricsSkeleton />
      ) : (
        <div>
          <div
            className={`title flex items-start ${
              isMobile || isTablet || isLaptop
                ? "flex-col gap-2 items-start"
                : "items-center justify-between"
            } py-3 px-4 border-b-2`}
          >
            <h4 className="whitespace-nowrap">
              <MuiTooltipWrapper title="Distribution of users by role">
                <span>User Breakdown</span>
              </MuiTooltipWrapper>
            </h4>

            <DateFilterComponent
              isDashboard={true}
              label="Select Date"
              selectLabel="Filter By"
              value={selectedDate}
              onChange={handleDateChange}
              availableViews={VIEW_OPTIONS}
              defaultView={viewDateMode}
              onViewChange={(val) => setViewDateMode(val)}
              containerSx={{
                width: "100%",
                display: "flex",
                justifyContent:
                  isMobile || isTablet || isLaptop ? "flex-start" : "flex-end",
                "& > .MuiFormControl-root": {
                  marginRight: { xs: "0", sm: "0px !important" },
                  width: { xs: "100%", sm: "auto" },
                },
              }}
            />
          </div>

          <div
            className="flex items-center justify-center px-4"
            role="region"
            aria-label="User Breakdown Chart"
          >
            {isFetching ? (
              <Skeleton
                variant="circular"
                width={240}
                height={240}
                sx={{ bgcolor: "grey.200", marginTop: 5, marginBottom: 5 }}
                aria-label="Loading user breakdown chart"
              />
            ) : (
              <div
                style={{
                  height: "300px",
                  width: "100%",
                }}
              >
                <DoughnutChart
                  data={chartData}
                  colors={USER_BREAKDOWN_COLORS}
                  cutout={40}
                  aria-label="User Breakdown Chart"
                  onSliceClick={(dataIndex) => {
                  const label = userDistribution?.breakdown?.[dataIndex]?.label;
                  handleUserBreakdownClick(label);
                }}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserBreakdownChart;
